// =============================================
// Entity & EAV Normalizer — uniwersalny, bez hardcoded domen
// Działa na 500+ tematyk. Zero domenowych aliasów w globalnym core.
// =============================================

import type { LLMEntityOutput, LLMEAVOutput } from "@/types/thematicMaps";

// =============================================
// Polish diacritics
// =============================================

const PL_MAP: Record<string, string> = {
  ą: "a", ć: "c", ę: "e", ł: "l", ń: "n",
  ó: "o", ś: "s", ź: "z", ż: "z",
  Ą: "a", Ć: "c", Ę: "e", Ł: "l", Ń: "n",
  Ó: "o", Ś: "s", Ź: "z", Ż: "z",
};

function stripPolish(text: string): string {
  return text.replace(/[ąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/g, (ch) => PL_MAP[ch] || ch);
}

// =============================================
// 1a. normalizeEntityName — klucz do porównań
// =============================================

export function normalizeEntityName(name: string): string {
  let norm = name.toLowerCase().trim();
  norm = stripPolish(norm);
  norm = norm.replace(/\s+/g, " ").trim();
  return norm;
}

// =============================================
// 1c. Pseudo-entity filter — ONLY standalone generic properties
// =============================================

const PSEUDO_ENTITIES_EXACT = new Set([
  "cena", "kolor", "temperatura", "proporcje", "wilgotnosc",
  "chrupkosc", "grubosc", "twardosc", "miekkosc",
  "rozmiar", "waga", "wysokosc", "szerokosc", "dlugosc",
  "zapach", "tekstura", "konsystencja", "gestosc",
  "szybkosc", "czestotliwosc", "ilosc", "objetosc",
  "jakosc", "poziom", "stopien", "intensywnosc",
]);

function isPseudoEntity(normalizedName: string): boolean {
  return PSEUDO_ENTITIES_EXACT.has(normalizedName);
}

// =============================================
// 1b. Alias cross-match helpers (konserwatywny)
// =============================================

function isValidAliasForMerge(alias: string): boolean {
  if (alias.length < 4) return false;
  const words = alias.split(" ").filter((w) => w.length > 0);
  if (words.length < 2) return false; // single-word aliases too generic
  return true;
}

function hasCanonicalConflict(canonicalA: string, canonicalB: string): boolean {
  const normA = normalizeEntityName(canonicalA);
  const normB = normalizeEntityName(canonicalB);
  // Conflict: both are long (>=5 chars) and DIFFERENT after normalization
  return normA.length >= 5 && normB.length >= 5 && normA !== normB;
}

// =============================================
// 1b+1c. mergeEntitiesByNormalization — main merge
// =============================================

export function mergeEntitiesByNormalization(
  entities: LLMEntityOutput[],
): LLMEntityOutput[] {
  // ---- WARSTWA 1: Exact normalized match ----
  const groups = new Map<string, LLMEntityOutput[]>();
  for (const entity of entities) {
    const key = normalizeEntityName(entity.canonical_name);
    const group = groups.get(key) || [];
    group.push(entity);
    groups.set(key, group);
  }

  // Merge each group
  const afterLayer1: LLMEntityOutput[] = [];
  for (const [, group] of groups) {
    if (group.length === 1) {
      afterLayer1.push(group[0]);
      continue;
    }
    afterLayer1.push(mergeGroup(group));
  }

  // ---- WARSTWA 2: Alias cross-match (konserwatywny) ----
  const result: LLMEntityOutput[] = [];
  const merged = new Set<number>(); // indices already merged

  for (let i = 0; i < afterLayer1.length; i++) {
    if (merged.has(i)) continue;

    let current = afterLayer1[i];

    for (let j = i + 1; j < afterLayer1.length; j++) {
      if (merged.has(j)) continue;

      const other = afterLayer1[j];

      // Check: canonical A = alias of B (or vice versa)
      const shouldMerge = checkAliasCrossMatch(current, other);

      if (shouldMerge) {
        current = mergeGroup([current, other]);
        merged.add(j);
      }
    }

    result.push(current);
  }

  // ---- Pseudo-entity filter ----
  return result.filter((entity) => {
    const normalized = normalizeEntityName(entity.canonical_name);
    return !isPseudoEntity(normalized);
  });
}

/**
 * Check if two entities should merge via alias cross-match.
 * KONSERWATYWNY: przy wątpliwości POMIŃ merge.
 */
function checkAliasCrossMatch(a: LLMEntityOutput, b: LLMEntityOutput): boolean {
  const normCanonicalA = normalizeEntityName(a.canonical_name);
  const normCanonicalB = normalizeEntityName(b.canonical_name);

  // If canonicals conflict (both long and different) → don't merge
  if (hasCanonicalConflict(a.canonical_name, b.canonical_name)) {
    return false;
  }

  // Check: canonical A matches any alias of B
  for (const alias of b.aliases) {
    const normAlias = normalizeEntityName(alias);
    if (normAlias === normCanonicalA && isValidAliasForMerge(normAlias)) {
      return true;
    }
  }

  // Check: canonical B matches any alias of A
  for (const alias of a.aliases) {
    const normAlias = normalizeEntityName(alias);
    if (normAlias === normCanonicalB && isValidAliasForMerge(normAlias)) {
      return true;
    }
  }

  return false;
}

/**
 * Merge a group of entities into one.
 * Canonical: shorter name. Description: longest. Aliases: all unique.
 */
function mergeGroup(group: LLMEntityOutput[]): LLMEntityOutput {
  // Pick canonical: shorter, more neutral name
  const sorted = [...group].sort((a, b) => a.canonical_name.length - b.canonical_name.length);
  const chosenCanonical = sorted[0].canonical_name;

  const allAliases = new Set<string>();
  let bestDescription = "";
  let bestEntityType = group[0].entity_type;

  for (const entity of group) {
    if (entity.canonical_name.toLowerCase() !== chosenCanonical.toLowerCase()) {
      allAliases.add(entity.canonical_name);
    }
    for (const alias of entity.aliases) {
      if (alias.toLowerCase() !== chosenCanonical.toLowerCase()) {
        allAliases.add(alias);
      }
    }
    if (entity.description.length > bestDescription.length) {
      bestDescription = entity.description;
    }
    if (entity.entity_type !== "attribute") {
      bestEntityType = entity.entity_type;
    }
  }

  return {
    canonical_name: chosenCanonical,
    entity_type: bestEntityType,
    aliases: [...allAliases],
    description: bestDescription,
  };
}

// =============================================
// 1d. normalizeAttributeName — snake_case bez PL
// =============================================

export function normalizeAttributeName(name: string): string {
  let norm = name.toLowerCase().trim();
  norm = stripPolish(norm);
  norm = norm.replace(/[\s-]+/g, "_");
  norm = norm.replace(/_+/g, "_");
  norm = norm.replace(/^_|_$/g, "");
  return norm;
}

// =============================================
// 1e. normalizeEAVResults — dedup + normalize (PER ENCJA, bez cross-entity)
// =============================================

export function normalizeEAVResults(eavResults: LLMEAVOutput[]): LLMEAVOutput[] {
  const result: LLMEAVOutput[] = [];

  for (const eavEntity of eavResults) {
    // Normalize all attribute names
    const normalizedAttrs = eavEntity.attributes.map((attr) => ({
      ...attr,
      attribute_name: normalizeAttributeName(attr.attribute_name),
    }));

    // Dedup PER ENCJA: entity + normalized_attr → highest confidence wins
    const attrMap = new Map<string, typeof normalizedAttrs[0]>();
    for (const attr of normalizedAttrs) {
      const existing = attrMap.get(attr.attribute_name);
      if (!existing || attr.confidence > existing.confidence) {
        attrMap.set(attr.attribute_name, attr);
      }
    }

    // Confidence < 0.3 → odrzuć. Max 8 per encja.
    const limited = [...attrMap.values()]
      .filter((a) => a.confidence >= 0.3)
      .sort((a, b) => b.confidence - a.confidence)
      .slice(0, 8);

    if (limited.length > 0) {
      result.push({
        entity_canonical_name: eavEntity.entity_canonical_name,
        attributes: limited,
      });
    }
  }

  return result;
}
